import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Store } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentReturn() {
  const cancelled = window.location.pathname.includes("/payment/cancel");
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  const [state, setState] = useState(cancelled ? "cancel" : "polling");
  const [orderNo, setOrderNo] = useState(localStorage.getItem("pc_last_order") || "");

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        if (res.data.order_number) setOrderNo(res.data.order_number);
        if (res.data.payment_status === "paid") {
          setState("paid");
          localStorage.removeItem("pc_cart");
          localStorage.removeItem("pc_last_order");
          return;
        }
        if (res.data.status === "expired" || res.data.payment_status === "failed") {
          setState("failed");
          return;
        }
      } catch {}
      tries += 1;
      if (tries >= 10) return setState("failed");
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [cancelled, sessionId]);

  const home = () => (window.location.href = "/");

  return (
    <div className="min-h-screen bg-noir text-white noise relative grid place-items-center px-6" data-testid="payment-return">
      <div className="relative z-10 max-w-md w-full text-center rounded-[2rem] border border-white/10 bg-noir-soft p-10">
        <div className="mb-3 font-display text-xl font-extrabold uppercase tracking-tight">Protein<span className="text-amber-brand">Cafe</span></div>

        {state === "polling" && (
          <>
            <Loader2 size={56} className="mx-auto text-amber-brand animate-spin" />
            <h1 className="mt-5 font-display text-3xl font-bold">Confirming payment…</h1>
            <p className="mt-2 text-white/60">Hang tight while we verify with your bank.</p>
          </>
        )}

        {state === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 size={64} className="mx-auto text-[#DE9A2C]" />
            <h1 className="mt-5 font-display text-3xl font-bold">Payment confirmed!</h1>
            <p className="mt-2 text-white/70">Order <span className="font-mono font-bold text-white" data-testid="paid-order-number">{orderNo || "confirmed"}</span> is paid & reserved.</p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm">
              <Store size={16} className="text-amber-brand" /> Pick up at The Fuel Lounge™
            </div>
            {orderNo ? (
              <div className="mt-6">
                <a data-testid="track-order-link" href={`/track?order=${orderNo}`} className="rounded-full border border-white/20 hover:border-amber-brand text-white font-semibold px-6 py-2.5 transition-colors">
                  Track Order
                </a>
              </div>
            ) : null}
          </div>
        )}

        {state === "cancel" && (
          <div data-testid="payment-cancel">
            <XCircle size={64} className="mx-auto text-white/60" />
            <h1 className="mt-5 font-display text-3xl font-bold">Payment cancelled</h1>
            <p className="mt-2 text-white/60">No charge was made. Your cart is still saved.</p>
          </div>
        )}

        {state === "failed" && (
          <div data-testid="payment-failed">
            <XCircle size={64} className="mx-auto text-[#8A2231]" />
            <h1 className="mt-5 font-display text-3xl font-bold">Couldn't confirm payment</h1>
            <p className="mt-2 text-white/60">If you were charged, contact us and we'll sort it out.</p>
          </div>
        )}

        <button data-testid="payment-home" onClick={home} className="mt-8 rounded-full bg-[#DE9A2C] hover:bg-[#c9861f] text-[#17120C] font-bold px-8 py-3 transition-colors">
          Back to ProteinCafe
        </button>
      </div>
    </div>
  );
}
